/**
 * Multi-org prompt upload that preserves each org's On/Off placement.
 *
 * The uploaded payload is written to every target, but enabled/disabled path
 * is resolved per org (and the opposite path is cleared) so another facility
 * is never silently re-enabled or left with a dead duplicate entry.
 * @module uploadPromptAcrossOrgs
 */
import { AuthLetterAgentConfigService } from "../../../../api/services/authLetterAgentConfigService";
import { buildUploadFields, Dict, SECTIONS } from "./authLetterConfigUtils";
import { UploadedPrompt } from "./components/PayerConfigCard";

export interface UploadPromptAcrossOrgsResult {
  /** Orgs that received the upload successfully. */
  updatedOrgIds: string[];
  /** Orgs whose read/write failed. */
  failures: { orgId: string; message: string }[];
  /** Merged primary-org doc when the primary write succeeded. */
  primaryNextDoc: Dict | null;
}

/**
 * Uploads a prompt payload to each selected org with per-org On/Off placement.
 *
 * @param params.payer - Payer receiving the prompt
 * @param params.parsed - Uploaded prompt sections
 * @param params.targetOrgIds - Orgs to write to
 * @param params.primaryOrgId - Org whose local UI doc is mirrored
 * @param params.primaryDoc - Current in-memory primary org config
 * @return Per-org outcomes and optional primary next doc
 */
export const uploadPromptAcrossOrgs = async (params: {
  payer: string;
  parsed: UploadedPrompt;
  targetOrgIds: string[];
  primaryOrgId: string;
  primaryDoc: Dict;
}): Promise<UploadPromptAcrossOrgsResult> => {
  const { payer, parsed, primaryOrgId, primaryDoc } = params;
  const uniqueIds = Array.from(
    new Set(params.targetOrgIds.map((id) => id.trim()).filter(Boolean)),
  );

  const sections: Partial<Record<(typeof SECTIONS)[number], Dict>> = {};
  for (const sec of SECTIONS) {
    const uploaded = (parsed as Dict)[sec];
    if (uploaded === undefined) continue;
    sections[sec] = uploaded as Dict;
  }

  const failures: { orgId: string; message: string }[] = [];
  const updatedOrgIds: string[] = [];
  let primaryNextDoc: Dict | null = null;

  await Promise.all(
    uniqueIds.map(async (targetId) => {
      try {
        const orgDoc =
          targetId === primaryOrgId
            ? primaryDoc
            : await AuthLetterAgentConfigService.getRawConfig(targetId);
        const built = buildUploadFields(orgDoc, payer, sections);
        if (!built) return;
        const now = new Date().toISOString();
        built.fields.updated_at = now;
        built.nextDoc.updated_at = now;
        await AuthLetterAgentConfigService.updatePayerFields(
          targetId,
          built.fields,
        );
        updatedOrgIds.push(targetId);
        if (targetId === primaryOrgId) primaryNextDoc = built.nextDoc;
      } catch (e) {
        failures.push({
          orgId: targetId,
          message: e instanceof Error ? e.message : "Unknown error",
        });
      }
    }),
  );

  return { updatedOrgIds, failures, primaryNextDoc };
};
